import * as _ from "lodash";
import { DimensionType, IHierarchy, IHierarchyLevel, IHierarchyNode } from "../PA.Core/PA.Core.IF.Hierarchy";
import {
    WB_HierarchyRow,
    WB_PeriodHierarchyRow,
    WB_PeriodRelationshipRow,
    WB_PeriodRow,
    WB_PeriodTypeRow
} from "../PA.Core/PAdbSchemaRows";
import { MessageProvider } from "../PA.Core/Basic/Messages";
import { IMessageProvider, MessageType } from "../PA.Core/PA.Core.IF.Basic";
import { PAdbContext } from "../PA.Core/PAdbContext";
import {
    esWB_Hierarchy, esWB_Hierarchy_Level,
    esWB_Period,
    esWB_Period_Hierarchy,
    esWB_Period_Hierarchy_Level,
    esWB_Period_Relationship,
    esWB_Period_Type
} from "../PA.Core/PAdbSchema";
import { ESRecordResolver } from "../PA.Core/lib/ESRecordResolver";


export interface CreateOptions {
    dimType?: DimensionType
}

export class PeriodHierarchy
    implements IHierarchy {

    private _wbPeriodHId: number
    private _PeriodHId: string
    private _PeriodHName: string
    private dbContext: PAdbContext

    private dctHierarchyLevels: { [LevelId: string]: IHierarchyLevel }
    private dctNodes: { [NodeId: string]: IHierarchyNode }
    private dctNodesByLevel: { [LevelId: string]: IHierarchyNode[] }

    private dctNodeParentByChildId: { [NodeId: string]: IHierarchyNode }
    private dctNodeChildrenByParentId: { [NodeId: string]: IHierarchyNode[] }
    private arrTopLevelNodes: IHierarchyNode[]

    private _loadMessageProvider: MessageProvider;
    private _OnLoadComplete: Promise<void>
    private _bLoaded: boolean
    private _dimType: DimensionType


    constructor(wbPeriodHId: number, options?: CreateOptions) {
        let opts = options || {}
        this.dbContext = new PAdbContext()
        this._wbPeriodHId = wbPeriodHId;
        this._PeriodHId = "Not Constructed"
        this._PeriodHName = "Not Constructed"
        this._dimType = DimensionType.Time
        if ('dimType' in opts)
            this._dimType = opts.dimType

        this._bLoaded = false
        this._loadMessageProvider = new MessageProvider()
        this.dctHierarchyLevels = {}
        this.dctNodes = {}
        this.dctNodesByLevel = {}
        this.dctNodeParentByChildId = {}
        this.dctNodeChildrenByParentId = {}
        this.arrTopLevelNodes = []
    }


    get DimensionType(): DimensionType {
        return this._dimType;
    }

    get bHasSortOrder(): boolean {
        return true
    }

    get HierarchyId(): string {
        return this._wbPeriodHId + ''
    }

    get HierarchyIdUI(): string {
        return this._PeriodHId
    }

    get HierarchyName(): string {
        return this._PeriodHName
    }

    get LoadMessageProvider(): IMessageProvider {
        return this._loadMessageProvider
    }

    get bLoaded(): boolean {
        return this._bLoaded
    }


    get OnLoadComplete(): Promise<void> {
        const self = this

        if (self._bLoaded)
            return Promise.resolve()


        if (!_.isUndefined(self._OnLoadComplete))
            return self._OnLoadComplete

        let returnValue = self._OnLoadComplete = (async () => {
            let msg = ""
            let recordResolver = new ESRecordResolver(self.dbContext,
                {
                    standardConditions: {
                        [esWB_Period_Hierarchy_Level]: { DELETED_IND: { neq: ['1'] } },
                        [esWB_Period_Relationship]: { DELETED_IND: { neq: ['1'] } },
                    },
                    dctIncludeBelongsToNamedRelations: {
                        [esWB_Period]: [esWB_Period_Type]
                    }
                })

            self._loadMessageProvider.clearMessages()

            // Get the Hierarchy Record
            await recordResolver.findAllWithRelationships(esWB_Period_Hierarchy, [self._wbPeriodHId])
            let aPerdHRow: WB_PeriodHierarchyRow = <any>recordResolver.get(esWB_Period_Hierarchy, self._wbPeriodHId)

            if (_.isEmpty(aPerdHRow)) {
                msg = `Unknown Period Hierarchy ${self._wbPeriodHId}`
                self.LoadMessageProvider.addMessage(
                    { code: "MissingPerdH", type: MessageType.Error, title: msg, description: msg })
                return
            }

            self._PeriodHId = aPerdHRow.cif.WB_PERIOD_HIER_ID
            self._PeriodHName = aPerdHRow.cif.WB_PERIOD_HIER_NAME

            // Populate Levels from MetaData
            for (let aLevelRow of aPerdHRow.WB_PeriodHierarchyLevelRows) {
                let periodU = <WB_PeriodTypeRow>(await this.dbContext.findUsingCache(esWB_Period_Type, [aLevelRow.cif.WB_PERIODM_ID]))[0]
                let levelId = periodU?.cif?.PERIOD_TYPE_ID || "Unknown"

                if (levelId in self.dctHierarchyLevels) {
                    msg = `Period Hierarchy Level (${levelId}) duplicated`
                    self.LoadMessageProvider.addMessage(
                        { code: "DupPerdHLvl", type: MessageType.Warning, title: msg, description: msg })
                    continue // Next Level
                }

                self.dctHierarchyLevels[levelId] = {
                    LevelId: levelId,
                    LevelName: aLevelRow.cif.LEVEL_NAME,
                    LevelDepth: aLevelRow.cif.LEVEL_DEPTH
                }

            }

            // Read in the Relationship and Nodes
            let arrRelRows: WB_PeriodRelationshipRow[] = <WB_PeriodRelationshipRow[]>await recordResolver.queryAllWithRelationships(esWB_Period_Relationship, {
                WB_PERIODH_ID: this._wbPeriodHId,
                DELETED_IND: { neq: ['1'] }
            })

            //-- Sort by Child,  Key to make the results repeatable
            arrRelRows = _.sortBy(arrRelRows, ['WB_PERIOD_ID', 'WB_PERIODR_ID'])

            _.forEach(arrRelRows, (aRow) => {

                //-- Only look at rows that have valid parents and children
                if (_.isEmpty(aRow.WB_PeriodRowParent) || _.isEmpty(aRow.WB_PeriodRowChild))
                    return

                let parent = self.addNode(aRow.WB_PeriodRowParent)
                let child = self.addNode(aRow.WB_PeriodRowChild)
                self.addHierarchyChildParentRel(child, parent)

            })

            // Find Levels In Nodes but not in Hierarchy
            let tHierarchy:WB_HierarchyRow =_.first( <WB_HierarchyRow[]>await self.dbContext.findAll(esWB_Hierarchy, { where: { TARGET_WB_ID: self._wbPeriodHId } }, { with: [esWB_Hierarchy_Level] }))
            let dctLevelById = _.keyBy(_.filter(tHierarchy.WB_HierarchyLevelRows, r => r.isActive), "LEVEL_ID")

            _.forEach(this.dctNodesByLevel, (arrNodes, levelId) => {
                if (levelId in this.dctHierarchyLevels)
                    return


                // Create missing Level
                this.dctHierarchyLevels[levelId] = {
                    LevelId: levelId,
                    LevelName: levelId,
                    LevelDepth: dctLevelById[levelId]?.cif.LEVEL_DEPTH
                }
            })

            // Determine Top Level Nodes, nodes without Parents.
            _.forEach(this.dctNodes, (aNode, nodeId) => {
                if (!('ParentId' in aNode))
                    this.arrTopLevelNodes.push(aNode)
            })

            self._bLoaded = true
        })();


        return returnValue
    }

    private addNode(aPeriodRow: WB_PeriodRow): IHierarchyNode {
        //--
        if (_.isEmpty(aPeriodRow))
            return undefined

        let wbPerdId = aPeriodRow.cif.WB_PERIOD_ID + ''
        let levelId = aPeriodRow.WB_PeriodTypeRow?.cif?.PERIOD_TYPE_ID || "Unnknown"

        if (wbPerdId in this.dctNodes)
            return this.dctNodes[wbPerdId]

        let newNode: IHierarchyNode = {
            NodeId: wbPerdId,
            NodeIdUI: aPeriodRow.cif.PERIOD_ID,
            NodeName: aPeriodRow.cif.PERIOD_NAME,
            NodeLevel: levelId,
            NodeOrder: aPeriodRow.PeriodStartDt.getTime(),
            NodeShortName: aPeriodRow.cif.PERIOD_NAME
        }

        this.dctNodes[wbPerdId] = newNode

        if (levelId in this.dctNodesByLevel)
            this.dctNodesByLevel[levelId].push(newNode)
        else
            this.dctNodesByLevel[levelId] = [newNode]

        return this.dctNodes[wbPerdId]
    }

    private addHierarchyChildParentRel(aChild: IHierarchyNode, aParent: IHierarchyNode): void {

        let nodeIdChild = aChild.NodeId
        let nodeIdParent = aParent.NodeId

        if (nodeIdChild in this.dctNodeParentByChildId) {
            let msg = `Child ${aChild.NodeName} (${aChild.NodeId}) has duplicate Parent ${aParent?.NodeName} (${aParent?.NodeIdUI})`
            this._loadMessageProvider.addMessage({
                code: "DuplicateParent",
                type: MessageType.Warning,
                title: msg,
                description: msg
            })
            return
        }

        this.dctNodeParentByChildId[nodeIdChild] = aParent
        aChild.ParentId = aParent.NodeId

        if (nodeIdParent in this.dctNodeChildrenByParentId)
            this.dctNodeChildrenByParentId[nodeIdParent].push(aChild)
        else
            this.dctNodeChildrenByParentId[nodeIdParent] = [aChild]
    }

    getLevels(): IHierarchyLevel[] {
        return _.values(this.dctHierarchyLevels)
    }

    getTopLevelNodes(): IHierarchyNode[] {
        return this.arrTopLevelNodes;
    }

    getNode(NodeId: string): IHierarchyNode {

        if (NodeId in this.dctNodes)
            return this.dctNodes[NodeId]

        return undefined;
    }

    getNodes(): IHierarchyNode[] {
        return _.values(this.dctNodes)
    }

    getNodesAtLevel(HierarchyLevel: string): IHierarchyNode[] {
        if (HierarchyLevel in this.dctNodesByLevel)
            return this.dctNodesByLevel[HierarchyLevel]

        return []
    }

    getChildren(nodes: IHierarchyNode[] | IHierarchyNode | string[] | string, levelId?: string): IHierarchyNode[] {
        let arrNodeIds = this.convertToKeyArray(nodes)

        let bCheckLevel = !_.isEmpty(levelId)
        let returnValue: IHierarchyNode[] = []

        let arrRecurseOnChildren: IHierarchyNode[] = []
        _.forEach(arrNodeIds, (aNodeId) => {
            if (aNodeId in this.dctNodeChildrenByParentId) {
                let arrChildren = this.dctNodeChildrenByParentId[aNodeId]
                _.forEach(arrChildren, (aChildNode) => {
                    if (bCheckLevel) {
                        if (levelId == aChildNode.NodeLevel)
                            returnValue.push(aChildNode)
                        else
                            arrRecurseOnChildren.push(aChildNode)
                    } else {
                        returnValue.push(aChildNode)
                    }
                })
            }
        })

        // Recurse
        if (bCheckLevel && !_.isEmpty(arrRecurseOnChildren)) {
            let moreChildren = this.getChildren(arrRecurseOnChildren, levelId)
            _.forEach(moreChildren, (a) => returnValue.push(a))
        }

        return returnValue;
    }

    getParents(nodes: IHierarchyNode[] | IHierarchyNode | string[] | string, levelId?: string): {
        [NodeId: string]: IHierarchyNode
    } {
        let arrNodeIds = this.convertToKeyArray(nodes)

        let bCheckLevel = !_.isEmpty(levelId)
        let returnValue: { [NodeId: string]: IHierarchyNode } = {}

        let heightLimit = 20
        _.forEach(arrNodeIds, (aNodeId) => {
            let depth = 0
            let parent = this.dctNodeParentByChildId[aNodeId]
            if (bCheckLevel) {
                while (parent && (parent?.NodeLevel != levelId) && (depth < heightLimit)) {
                    parent = this.dctNodeParentByChildId[parent.NodeId]
                    depth += 1
                }
                if (depth == heightLimit)
                    parent = undefined
            }

            returnValue[aNodeId] = parent
        })

        return returnValue;
    }


    private convertToKeyArray(nodes: IHierarchyNode[] | IHierarchyNode | string[] | string): string[] {
        let returnValue: string[] = []

        if (_.isEmpty(nodes) && !_.isNumber(nodes))
            return returnValue

        let keyList: any[]
        if (_.isArray(nodes))
            keyList = nodes
        else
            keyList = [nodes]

        _.forEach(keyList, (aKeyItem) => {
            if (_.isString(aKeyItem)) {
                returnValue.push(aKeyItem)
                return
            }

            if (_.isNumber(aKeyItem))
                returnValue.push(aKeyItem + '')

            if (_.isEmpty(aKeyItem))
                return


            if ('NodeId' in aKeyItem)
                returnValue.push(aKeyItem.NodeId)
        })

        return returnValue
    }


}
